import { Children, type ReactNode } from "react";
import { MicroLabel } from "./Text";

/**
 * One thing an operator can change, as a ruled row: what it is, what it is now,
 * and the buttons that change it.
 *
 * The detail pages used to give every editable property a section of its own — a
 * heading, a paragraph, a row of buttons — so three small settings read as three
 * chapters and their buttons landed wherever each chapter happened to end. As rows
 * they share columns: labels on one edge, current values on one line, actions on
 * the other edge, and an operator scans down the right-hand side to find what they
 * can press.
 *
 * `children` is the row's working area — an edit form, a confirmation, a notice —
 * and sits under the value, so a form opens where the value it replaces was read.
 */
export function SettingRow({
  label,
  value,
  hint,
  actions,
  children,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-2 border-t border-rule py-4 first:border-t-0 first:pt-0 md:grid-cols-[13rem_minmax(0,1fr)_auto]">
      <MicroLabel className="pt-0.5">{label}</MicroLabel>
      <div className="flex max-w-prose flex-col gap-1">
        <div className="text-data text-ink">{value}</div>
        {hint === undefined ? null : <p className="m-0 text-data text-ink-muted">{hint}</p>}
      </div>
      <div className="flex flex-wrap items-start gap-3 md:justify-end">{actions}</div>
      {Children.toArray(children).length === 0 ? null : (
        <div className="flex flex-col gap-3 md:col-span-2 md:col-start-2">{children}</div>
      )}
    </div>
  );
}

/** A titled group of rows. */
export function SettingGroup({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4" aria-label={title}>
      <h2 className="border-b border-rule pb-2 text-section font-semibold text-ink">{title}</h2>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}
